/*
 * File: app.component.ts
 * Project: rast-app
 * Create Date: Tuesday, 15th July 2025
 * Author: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Last Modified: Tuesday, 28th July 2026 11:22:18 am
 * Modified by: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Copyright (c) 2026 CPA Wernher von Braun
 * ----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import { Component } from '@angular/core';
import { IonApp, IonRouterOutlet, IonToast, ModalController, Platform } from '@ionic/angular/standalone';
import { App } from '@capacitor/app';
import { addIcons } from 'ionicons';
import {
  addOutline,
  barcodeOutline,
  camera,
  chevronBackOutline,
  closeOutline,
  documentOutline,
  ellipsisVertical,
  eyeOffOutline,
  eyeOutline,
  fingerPrintOutline,
  flash,
  flashOff,
  giftOutline,
  globeOutline,
  homeOutline,
  imageOutline,
  informationCircleOutline,
  lockClosedOutline,
  mailOutline,
  personOutline,
  settingsOutline,
  shieldCheckmark,
  shieldCheckmarkOutline,
  trashOutline,
} from 'ionicons/icons';
import { ToastService } from './core/services/toast/toast.service';
import { VersionCheckService } from './core/services/version-check/version-check.service';
import { person, chevronDownSharp, chevronUpSharp } from 'ionicons/icons';

addIcons({
  person,
  chevronDownSharp,
  chevronUpSharp,
});

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonRouterOutlet, IonApp, IonToast],
})
export class AppComponent {
  isNativeApp: boolean = false;

  constructor(
    public readonly toastService: ToastService,
    private readonly platform: Platform,
    private readonly versionCheckService: VersionCheckService,
    private readonly modalController: ModalController
  ) {
    this.isNativeApp = this.platform.is('capacitor');

    if (this.isNativeApp) {
      this.versionCheckService.checkMinimumVersion();
      this.setupBackButtonHandler();
    }
    /**
     * Any icons you want to use in your application
     * can be registered in app.component.ts and then
     * referenced by name anywhere in your application.
     */
    addIcons({
      'mail-outline': mailOutline,
      'lock-closed-outline': lockClosedOutline,
      'eye-outline': eyeOutline,
      'eye-off-outline': eyeOffOutline,
      'person-outline': personOutline,
      'document-outline': documentOutline,
      'trash-outline': trashOutline,
      'chevron-back-outline': chevronBackOutline,
      'image-outline': imageOutline,
      'camera': camera,
      'shield-checkmark': shieldCheckmark,
      'close-outline': closeOutline,
      'barcode-outline': barcodeOutline,
      'shield-checkmark-outline': shieldCheckmarkOutline,
      'home-outline': homeOutline,
      'globe-outline': globeOutline,
      'settings-outline': settingsOutline,
      'flash': flash,
      'flash-off': flashOff,
      'person': person,
      'chevron-down-sharp': chevronDownSharp,
      'chevron-up-sharp': chevronUpSharp,
      'gift-outline': giftOutline,
      'add-outline': addOutline,
      'information-circle-outline': informationCircleOutline,
      'finger-print-outline': fingerPrintOutline,
      'ellipsis-vertical': ellipsisVertical
    });
  }

  private setupBackButtonHandler(): void {
    App.addListener('backButton', async ({ canGoBack }) => {
      const topModal = await this.modalController.getTop();

      if (topModal) {
        await topModal.dismiss();
        return;
      }

      if (canGoBack) {
        window.history.back();
        return;
      }

      App.exitApp();
    });
  }
}
